import React from 'react'
import {
  VStack,
  FormControl,
  FormLabel,
  Select,
  HStack,
  Input,
  Button,
  Text,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
} from '@chakra-ui/react'
import { ResearchTag } from '../../../../types/Research'

interface TagManagementSectionProps {
  tags: ResearchTag[]
  selectedTags: string[]
  newTagName: string
  showTagError: boolean
  saving: boolean
  setNewTagName: (name: string) => void
  handleSelectTag: (tagId: string) => void
  handleRemoveTag: (tagId: string) => void
  handleAddNewTag: () => void
  setShowTagError: (show: boolean) => void
}

export const TagManagementSection: React.FC<TagManagementSectionProps> = ({
  tags,
  selectedTags,
  newTagName,
  showTagError,
  saving,
  setNewTagName,
  handleSelectTag,
  handleRemoveTag,
  handleAddNewTag,
  setShowTagError,
}) => {
  return (
    <FormControl>
      <FormLabel>Tags *</FormLabel>
      <VStack spacing={4} align='stretch'>
        {/* Display Selected Tags */}
        {selectedTags.length > 0 && (
          <VStack spacing={2} align='stretch'>
            <Text fontSize='sm' fontWeight='medium' color='gray.600'>
              Selected Tags:
            </Text>
            <Wrap spacing={2}>
              {selectedTags.map((tagId) => {
                const tag = tags.find((t) => t.id === tagId)
                return tag ? (
                  <WrapItem key={tag.id}>
                    <Tag size='md' colorScheme='blue' variant='solid'>
                      <TagLabel>{tag.name}</TagLabel>
                      <TagCloseButton onClick={() => handleRemoveTag(tag.id)} />
                    </Tag>
                  </WrapItem>
                ) : null
              })}
            </Wrap>
          </VStack>
        )}

        {/* Select Existing Tag */}
        <Select
          placeholder='Select an existing tag'
          onChange={(e) => {
            if (e.target.value) {
              handleSelectTag(e.target.value)
              e.target.value = '' // Reset select
              setShowTagError(false) // Clear error when tag is selected
            }
          }}
          mb={3}
        >
          {tags
            .filter((tag) => !selectedTags.includes(tag.id))
            .map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
        </Select>

        {/* Create New Tag */}
        <HStack>
          <Input
            placeholder='Create a new tag'
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddNewTag()
              }
            }}
          />
          <Button
            onClick={handleAddNewTag}
            isDisabled={!newTagName.trim() || saving}
            isLoading={saving}
          >
            Add Tag
          </Button>
        </HStack>

        {/* Only show error when form has been submitted and no tags selected */}
        {showTagError && selectedTags.length === 0 && (
          <Text fontSize='sm' color='red.500' mt={2}>
            At least one tag is required
          </Text>
        )}
      </VStack>
    </FormControl>
  )
}

export default TagManagementSection
